import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../hooks/useAuth';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from '../../components/common/Button';
import { useTheme } from '../../hooks/useTheme';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import { subscriptionAPI } from '../../api/subscriptions';
import { SubscriptionPlan } from '../../types/auth';
import { hexToRgba } from '../../utils/colorUtils';
import { useFeatureFlag, isFeatureEnabled } from '../../hooks/useFeatureFlags';
import { useEffect } from 'react';

interface PricingPlan {
  id: SubscriptionPlan;
  name: string;
  description: string;
  price: string;
  originalPrice?: string;
  period: string;
  features: string[];
  isPopular?: boolean;
  isCurrent?: boolean;
  isTrial?: boolean;
  trialDays?: number;
}

const DEFAULT_PLANS: Omit<PricingPlan, 'isCurrent'>[] = [
  {
    id: SubscriptionPlan.FREE,
    name: 'Free',
    description: 'Everything you need to discover your colours',
    price: 'Free',
    period: 'forever',
    features: [
      '3 colour analyses per month',
      'Virtual Drape (core swatches)',
      'Colour Library — browse & copy',
      'Palette Generator (basic)',
      'Limited wardrobe (3 items)',
    ],
  },
  {
    id: SubscriptionPlan.PLUS,
    name: 'Plus',
    description: 'Unlimited power for personal style',
    price: '$4.99',
    period: 'per month',
    features: [
      'Unlimited colour analyses',
      'Full Virtual Drape library',
      'Save & export palettes',
      'Analysis history',
      'Unlimited wardrobe',
    ],
    isPopular: true,
  },
  {
    id: SubscriptionPlan.PRO,
    name: 'Pro',
    description: 'The full suite — creators & professionals',
    price: '$9.99',
    period: 'per month',
    features: [
      'Everything in Plus',
      'Advanced AI model',
      'Early access to new features',
      'Downloadable PDF reports',
      'Priority support',
    ],
  },
];

const PricingScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { subscriptionPlan, refreshUser } = useAuth();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { data: pricingFlag, isLoading: isLoadingFlag } = useFeatureFlag(
    'pricing_subscription'
  );
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [plansFromApi, setPlansFromApi] = useState<
    Record<string, { price_display?: string; period?: string }>
  >({});

  // Fetch plan pricing from API (single source of truth with backend subscription plan)
  useEffect(() => {
    subscriptionAPI
      .getPlans()
      .then((res) => {
        const pricing: Record<
          string,
          { price_display?: string; period?: string }
        > = {};
        if (res.plans) {
          Object.entries(res.plans).forEach(([id, p]) => {
            if (p && typeof p === 'object' && 'price_display' in p) {
              pricing[id] = {
                price_display: (p as { price_display?: string }).price_display,
                period: (p as { period?: string }).period,
              };
            }
          });
        }
        setPlansFromApi(pricing);
      })
      .catch(() => {});
  }, []);

  // Redirect if feature flag is enabled (hide pricing when flag is enabled)
  useEffect(() => {
    if (
      !isLoadingFlag &&
      pricingFlag &&
      isFeatureEnabled(pricingFlag, 'pricing_subscription')
    ) {
      // Feature flag is enabled, hide pricing - go back
      navigation.goBack();
    }
  }, [pricingFlag, isLoadingFlag, navigation]);

  const pricingPlans: PricingPlan[] = DEFAULT_PLANS.map((base) => {
    const apiPlan = plansFromApi[base.id];
    return {
      ...base,
      price: apiPlan?.price_display ?? base.price,
      period: apiPlan?.period ?? base.period,
      isCurrent: subscriptionPlan === base.id,
    };
  });

  const handlePlanSelection = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      Alert.alert('Error', 'Please select a plan first');
      return;
    }

    try {
      setIsLoading(true);

      if (selectedPlan === SubscriptionPlan.FREE) {
        await subscriptionAPI.downgradeToFree();
        await refreshUser();
        Alert.alert('Success', 'Successfully downgraded to Free Plan');
        navigation.goBack();
        return;
      }

      if (
        selectedPlan === SubscriptionPlan.PLUS ||
        selectedPlan === SubscriptionPlan.PRO
      ) {
        const planId = selectedPlan === SubscriptionPlan.PLUS ? 'plus' : 'pro';
        try {
          const { checkout_url } = await subscriptionAPI.checkout({
            plan_id: planId,
            interval: 'monthly',
          });
          if (checkout_url) {
            const canOpen = await Linking.canOpenURL(checkout_url);
            if (canOpen) {
              await Linking.openURL(checkout_url);
              Alert.alert(
                'Complete payment',
                'Finish payment in the browser, then return here. Your plan will update automatically.'
              );
            } else {
              Alert.alert(
                'Error',
                'Could not open checkout. Please try again.'
              );
            }
            return;
          }
        } catch (checkoutErr: unknown) {
          const err = checkoutErr as { response?: { status?: number } };
          if (err?.response?.status !== 503) throw checkoutErr;
          // Paddle not configured: use stub upgrade
        }
        if (selectedPlan === SubscriptionPlan.PLUS) {
          await subscriptionAPI.upgradeToPlus();
          await refreshUser();
          Alert.alert('Success', 'Successfully upgraded to Plus!');
        } else {
          await subscriptionAPI.upgradeToPro();
          await refreshUser();
          Alert.alert('Success', 'Successfully upgraded to Pro!');
        }
        navigation.goBack();
        return;
      }

      Alert.alert('Error', 'Invalid plan selected');
    } catch (error: unknown) {
      Alert.alert(
        'Error',
        error instanceof Error ? error.message : 'Failed to update subscription'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentPlanText = (plan: PricingPlan) => {
    if (plan.isCurrent) {
      if (plan.isTrial) {
        return 'Current Trial';
      }
      return 'Current Plan';
    }
    return null;
  };

  const getActionButtonText = (plan: PricingPlan) => {
    if (plan.isCurrent) {
      return 'Current Plan';
    }

    switch (plan.id) {
      case SubscriptionPlan.FREE:
        return 'Downgrade to Free';
      case SubscriptionPlan.PLUS:
        return 'Upgrade to Plus';
      case SubscriptionPlan.PRO:
        return 'Upgrade to Pro';
      default:
        return 'Select Plan';
    }
  };

  const isPlanDisabled = (plan: PricingPlan) => {
    return plan.isCurrent || isLoading;
  };

  // Show loading while checking feature flag
  if (isLoadingFlag) {
    return (
      <ResponsiveContainer style={{ backgroundColor: colors.background }}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </ResponsiveContainer>
    );
  }

  // Hide screen if feature flag is enabled
  if (isFeatureEnabled(pricingFlag, 'pricing_subscription')) {
    return null;
  }

  return (
    <LinearGradient
      colors={[
        hexToRgba(colors.appGradient1, 0.8),
        hexToRgba(colors.appGradient2, 0.8),
      ]}
      style={styles.container}
      start={{ x: 0.5, y: 0.5 }}
      end={{ x: 1, y: 0.5 }}
    >
      <ResponsiveContainer style={styles.responsiveContainer}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={{
            paddingBottom: 40 + insets.bottom,
            paddingHorizontal: 16,
            paddingTop: 16,
          }}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.header}>
            <Text style={[styles.title, { color: colors.text }]}>
              Choose Your Plan
            </Text>
            <Text style={[styles.subtitle, { color: colors.secondary }]}>
              Select the perfect plan for your colour analysis needs
            </Text>
          </View>

          {/* Pricing Plans */}
          <View style={styles.plansContainer}>
            {pricingPlans.map((plan) => (
              <View
                key={plan.id}
                style={[
                  styles.planCard,
                  {
                    backgroundColor: colors.card,
                    borderColor:
                      selectedPlan === plan.id ? colors.primary : colors.border,
                    borderWidth: selectedPlan === plan.id ? 2 : 1,
                    ...(Platform.OS === 'web'
                      ? {
                          boxShadow: `0px 2px 8px 0px ${colors.cardText}1A`,
                        }
                      : { shadowColor: colors.cardText }),
                  },
                  plan.isPopular && styles.popularPlan,
                ]}
              >
                {/* Popular Badge */}
                {plan.isPopular && (
                  <View
                    style={[
                      styles.popularBadge,
                      { backgroundColor: colors.primary },
                    ]}
                  >
                    <Text
                      style={[styles.popularText, { color: colors.background }]}
                    >
                      Most Popular
                    </Text>
                  </View>
                )}

                {/* Current Plan Badge */}
                {plan.isCurrent && (
                  <View
                    style={[
                      styles.currentBadge,
                      { backgroundColor: colors.success },
                    ]}
                  >
                    <Text
                      style={[styles.currentText, { color: colors.background }]}
                    >
                      {getCurrentPlanText(plan)}
                    </Text>
                  </View>
                )}

                {/* Plan Header */}
                <View style={styles.planHeader}>
                  <Text style={[styles.planName, { color: colors.text }]}>
                    {plan.name}
                  </Text>
                  <Text
                    style={[
                      styles.planDescription,
                      { color: colors.secondary },
                    ]}
                  >
                    {plan.description}
                  </Text>
                </View>

                {/* Pricing */}
                <View style={styles.pricingContainer}>
                  <View style={styles.priceRow}>
                    <Text style={[styles.price, { color: colors.text }]}>
                      {plan.price}
                    </Text>
                    {plan.originalPrice && (
                      <Text
                        style={[
                          styles.originalPrice,
                          { color: colors.secondary },
                        ]}
                      >
                        {plan.originalPrice}
                      </Text>
                    )}
                  </View>
                  <Text style={[styles.period, { color: colors.secondary }]}>
                    {plan.period}
                  </Text>
                </View>

                {/* Features */}
                <View style={styles.featuresContainer}>
                  {plan.features.map((feature, index) => (
                    <View key={index} style={styles.featureItem}>
                      <Ionicons
                        name="checkmark-circle"
                        size={16}
                        color={colors.success}
                      />
                      <Text
                        style={[styles.featureText, { color: colors.text }]}
                      >
                        {feature}
                      </Text>
                    </View>
                  ))}
                </View>

                {/* Action Button */}
                <Button
                  title={getActionButtonText(plan)}
                  onPress={() => handlePlanSelection(plan.id)}
                  disabled={isPlanDisabled(plan)}
                  variant="primary"
                  style={{
                    ...styles.actionButton,
                    backgroundColor: plan.isCurrent
                      ? colors.border
                      : colors.primary,
                    opacity: isPlanDisabled(plan) ? 0.6 : 1,
                  }}
                  textStyle={{
                    ...styles.actionButtonText,
                    color: plan.isCurrent
                      ? colors.secondary
                      : colors.background,
                  }}
                  trackClick={true}
                  trackingLocation="pricing-screen"
                  trackingData={{
                    button_name: getActionButtonText(plan),
                    button_type: getActionButtonText(plan),
                    screen: 'pricing',
                  }}
                />
              </View>
            ))}

            {/* Additional Info */}
            <View
              style={[
                styles.infoContainer,
                {
                  backgroundColor: colors.card,
                  borderColor: colors.border,
                  ...(Platform.OS === 'web'
                    ? {
                        boxShadow: `0px 2px 8px 0px ${colors.cardText}1A`,
                      }
                    : { shadowColor: colors.cardText }),
                },
              ]}
            >
              <Text style={[styles.infoTitle, { color: colors.text }]}>
                What&apos;s included?
              </Text>
              <Text style={[styles.infoText, { color: colors.secondary }]}>
                All plans include secure payment processing, 24/7 support, and
                the ability to cancel anytime. Your subscription will
                automatically renew unless cancelled before the renewal date.
              </Text>
            </View>
          </View>

          {/* Subscribe Button */}
          {selectedPlan &&
            !pricingPlans.find((p) => p.id === selectedPlan)?.isCurrent && (
              <View style={styles.subscribeContainer}>
                <Button
                  title={isLoading ? 'Processing...' : 'Subscribe Now'}
                  onPress={handleSubscribe}
                  loading={isLoading}
                  disabled={isLoading}
                  trackClick={true}
                  trackingLocation="pricing-screen"
                  trackingData={{
                    button_name: 'Subscribe Now',
                    button_type: 'subscribe_now',
                    screen: 'pricing',
                  }}
                />
              </View>
            )}

          {/* Loading Overlay */}
          {isLoading && (
            <View style={styles.loadingOverlay}>
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={[styles.loadingText, { color: colors.text }]}>
                Processing subscription...
              </Text>
            </View>
          )}
        </ScrollView>
      </ResponsiveContainer>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  responsiveContainer: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
  },
  plansContainer: {
    gap: 16,
  },
  planCard: {
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    position: 'relative',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  popularPlan: {
    borderWidth: 2,
  },
  popularBadge: {
    position: 'absolute',
    top: -12,
    left: 24,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  popularText: {
    fontSize: 12,
    fontWeight: '600',
  },
  currentBadge: {
    position: 'absolute',
    top: -12,
    right: 24,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  currentText: {
    fontSize: 12,
    fontWeight: '600',
  },
  planHeader: {
    marginBottom: 16,
  },
  planName: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  planDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  pricingContainer: {
    marginBottom: 24,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 4,
  },
  price: {
    fontSize: 32,
    fontWeight: '700',
    marginRight: 8,
  },
  originalPrice: {
    fontSize: 16,
    textDecorationLine: 'line-through',
  },
  period: {
    fontSize: 14,
  },
  featuresContainer: {
    marginBottom: 24,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  featureText: {
    marginLeft: 8,
    fontSize: 14,
    lineHeight: 20,
  },
  actionButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  subscribeContainer: {
    marginTop: 24,
    marginBottom: 32,
  },
  infoContainer: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    lineHeight: 20,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default PricingScreen;
